/**
 * Quiz popup behaviour (RevenueHunt)
 *
 * The app embed only toggles `body.revenuehunt-popup-open` when the quiz modal
 * opens. Everything else about living inside this theme is left to us:
 *
 *   1. Scroll lock — mirror the app class onto `html.scroll-locked`, reusing the
 *      theme's own lock (`overflow: hidden` plus the scrollbar gutter in
 *      theme.css, and `lenis.stop()` from the scroll lock manager in
 *      utilities.js) and add `html.quiz-popup-open` for quiz-popup.css.
 *   2. Chat launcher — hide it while the popup is open, it sits on top of the
 *      quiz's "Proceed to cart" bar. The chat is Daktela, injected at runtime by
 *      custom.js. Anything else needing the same treatment can opt in with a
 *      `data-hide-on-quiz-popup` attribute.
 *   3. Cart sync — the quiz posts to /cart/add.js itself and fires no event, so
 *      the cart bubble and drawer would keep showing the pre-quiz cart until the
 *      next page load.
 */
(() => {
  const POPUP_CLASS = "revenuehunt-popup-open";
  const LOCK_CLASS = "scroll-locked";
  const OWNER_CLASS = "quiz-popup-open";

  const CHAT_SELECTOR = [
    "#daktela-web", // Daktela widget root — see custom.js
    ".dw-button", // its launcher bubble, in case it mounts outside the root
    "[data-hide-on-quiz-popup]",
  ].join(",");

  const root = document.documentElement;

  let popupOpen = false;

  const hidden = new Map();

  const hideChatWidgets = () => {
    document.querySelectorAll(CHAT_SELECTOR).forEach((element) => {
      if (hidden.has(element)) return;

      hidden.set(element, element.style.getPropertyValue("display"));
     
      element.style.setProperty("display", "none", "important");
    });
  };

  const showChatWidgets = () => {
    hidden.forEach((display, element) => {
      if (display) {
        element.style.setProperty("display", display);
      } else {
        element.style.removeProperty("display");
      }
    });

    hidden.clear();
  };

  let pending = false;

  const lateMountObserver = new MutationObserver((mutations) => {
    if (pending) return;

    const relevant = mutations.some(
      (mutation) => !(mutation.target instanceof Element) || !mutation.target.closest("#quiz-container")
    );

    if (!relevant) return;

    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      hideChatWidgets();
    });
  });

  /* ---------------------------------------------------------------- cart --*/

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?($|\?)/;

  /**
   * Tells the theme the cart changed underneath it.
   *
   * Without a `sections` payload cart-items re-renders its section from the
   * server, which is what repopulates the drawer. Auto-open is suppressed for
   * the dispatch: the shopper is mid-quiz and the quiz has its own cart bar, so
   * throwing the drawer over the modal would just interrupt them.
   *
   * @param {Object} cart - The cart, as returned by /cart.js.
   */
  const dispatchCartUpdate = (cart) => {
    const drawer = document.querySelector("cart-drawer-component[auto-open]");

    drawer?.removeAttribute("auto-open");

    document.dispatchEvent(
      new CustomEvent("cart:update", {
        detail: {
          resource: cart,
          sourceId: "quiz-popup",
          data: {
            itemCount: cart.item_count,
            source: "quiz-popup",
          },
        },
      })
    );

    // Listeners are synchronous, so the drawer is back to normal for the next
    // add-to-cart outside the quiz.
    drawer?.setAttribute("auto-open", "");
  };

  const syncCart = async () => {
    try {
      const response = await fetch(window.FoxTheme?.routes?.cart ?? "/cart.js", {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return;

      dispatchCartUpdate(await response.json());
    } catch {
      // Offline or navigating away — the next page load renders the real cart.
    }
  };

  // "Add all to cart" fires one request per line; wait for the burst to finish.
  let syncTimer = null;

  const scheduleCartSync = () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncCart, 50);
  };

  /**
   * The quiz renderer talks to /cart/add.js directly and emits nothing we can
   * listen to, so its requests are picked up from fetch. Only requests made
   * while the popup is open count — the theme announces its own cart changes.
   */
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const result = originalFetch.apply(this, args);

    if (!popupOpen) return result;

    const [input] = args;
    const url = typeof input === "string" ? input : (input?.url ?? String(input));

    if (CART_MUTATION.test(url)) {
      result.then((response) => response.ok && scheduleCartSync()).catch(() => {});
    }

    return result;
  };

  /* --------------------------------------------------------------- state --*/

  const sync = () => {
    const isOpen = document.body.classList.contains(POPUP_CLASS);

    if (isOpen === root.classList.contains(OWNER_CLASS)) return;

    popupOpen = isOpen;
    root.classList.toggle(OWNER_CLASS, isOpen);

    if (isOpen) {
      root.classList.add(LOCK_CLASS);
      hideChatWidgets();
      lateMountObserver.observe(document.body, { childList: true, subtree: true });
      return;
    }

    lateMountObserver.disconnect();
    showChatWidgets();

    // A theme dialog opened underneath the quiz keeps its own lock — dialog.js
    // marks those with the `scroll-lock` attribute, so leave the class alone.
    if (!root.hasAttribute("scroll-lock")) {
      root.classList.remove(LOCK_CLASS);
    }
  };

  new MutationObserver(sync).observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });

  sync();
})();
